import React from 'react';
import styled from 'styled-components';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthRequest } from '../../services/apis/auth/useAuthRequest';
import { useAuthStore } from '../../stores/auth/useAuthStore';
import { AppRoutes } from '../../interfaces/store/appStore';
import { useAuthHandlers } from '../../services/actions/auth/useAuthHandlers';
import { IInviteLandingResponse, INVITE_ERROR_STATUSES, getInviteUnavailableContactHint, getInviteUnavailableMessage, isInviteLandingResponse, isInviteLoginLanding, isInvitePendingRegistration } from '../../interfaces/models/invite';
import { AuthPageContainer, AuthInnerContainer, AuthColumn, FullLogo, HeaderSection, AuthCard, AuthTitle, AuthSubtitle, AuthMethodButton } from '../authentication/authFormCommon';
import InviteLoadingState from './InviteLoadingState';
import ActionText from '../../components/ActionText';
import { useAppHandlers } from '../../services/actions/app/useAppHandlers';

const getPageTitle = (landing: IInviteLandingResponse): string => {
    switch (landing.status) {
        case 'accepted':
            return "You've joined the workspace";
        case 'already_member':
            return "You're already in this workspace";
        case 'already_accepted':
            return 'Invite already accepted';
        default:
            return 'Workspace invite';
    }
};

const getWelcomeMessage = (landing: IInviteLandingResponse): string => {
    const workspaceName = landing.invite?.workspace?.name || 'the workspace';

    switch (landing.status) {
        case 'accepted':
            return `Welcome to ${workspaceName}. You can now collaborate with your team, discover shared connections, and manage relationships together.`;
        case 'already_member':
            return `You're already a member of ${workspaceName}. Log in to continue to your workspace.`;
        case 'already_accepted':
            return landing.message || `This invite to ${workspaceName} was already accepted. Log in to continue.`;
        default:
            return landing.message;
    }
};

const InviteContactHint: React.FC<{ landing: IInviteLandingResponse }> = ({ landing }) => {
    const { showSuccessToast } = useAppHandlers();
    const hint = getInviteUnavailableContactHint(landing);

    if (hint.type === 'plain') {
        return <AuthSubtitle>{hint.text}</AuthSubtitle>;
    }

    const handleCopyEmail = async () => {
        try {
            await navigator.clipboard.writeText(hint.email);
            showSuccessToast({ message: 'Email copied' });
        } catch {
            /* clipboard unavailable */
        }
    };

    return (
        <AuthSubtitle as="div">
            {hint.before}
            <StyledInlineActionText onClickHandler={handleCopyEmail} $regular>
                {hint.email}
            </StyledInlineActionText>
            {hint.after}
        </AuthSubtitle>
    );
};

const Invite = React.forwardRef((props: any, ref: any) => {
    const [loaded, setLoaded] = React.useState(false);
    const [error, setError] = React.useState('');
    const [errorLanding, setErrorLanding] = React.useState<IInviteLandingResponse | null>(null);
    const [landing, setLanding] = React.useState<IInviteLandingResponse>();
    const [searchParams] = useSearchParams();
    const { invitePublic } = useAuthRequest();
    const authStore = useAuthStore();
    const { logoutHandler } = useAuthHandlers();
    const navigate = useNavigate();

    const authRef = React.useRef(authStore);
    authRef.current = authStore;

    const inviteCode = searchParams.get('invite');

    React.useEffect(() => {
        let cancelled = false;

        const init = async () => {
            if (!inviteCode) {
                setError('Invalid invite link.');
                setLoaded(true);
                return;
            }

            const response = await invitePublic(inviteCode);
            if (cancelled) return;

            if (!isInviteLandingResponse(response)) {
                setError('Something went wrong. Please try again.');
                setLoaded(true);
                return;
            }

            if (authRef.current.store.user) {
                await logoutHandler(`${window.location.pathname}${window.location.search}`);
                if (cancelled) return;
            }

            if (isInvitePendingRegistration(response)) {
                localStorage.setItem('inviteValue', JSON.stringify({
                    inviteCode,
                    landing: response,
                }));
                navigate(`${AppRoutes.SIGNUP}?invite=${inviteCode}`);
                return;
            }

            if (INVITE_ERROR_STATUSES.includes(response.status)) {
                setError(getInviteUnavailableMessage(response));
                setErrorLanding(response);
                setLoaded(true);
                return;
            }

            if (!isInviteLoginLanding(response)) {
                setError('Something went wrong. Please try again.');
                setLoaded(true);
                return;
            }

            setLanding(response);
            setLoaded(true);
        };

        init();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteCode]);

    const onContinueClick = React.useCallback(() => {
        navigate(AppRoutes.SIGNIN, {
            state: {
                fromInviteLogin: true,
                authMethod: landing?.authMethod ?? null,
                inviteEmail: landing?.invite?.email ?? null,
            },
        });
    }, [navigate, landing?.authMethod, landing?.invite?.email]);

    const content = React.useMemo(() => {
        if (!loaded) {
            return <InviteLoadingState />;
        }

        if (error) {
            return (
                <AuthColumn>
                    <FullLogo />
                    <HeaderSection>
                        <AuthTitle>Invite not available</AuthTitle>
                        <AuthSubtitle>{error}</AuthSubtitle>
                        {errorLanding && <InviteContactHint landing={errorLanding} />}
                    </HeaderSection>
                    <InviteCard>
                        <AuthMethodButton
                            $type="blue"
                            tooltipMessage=""
                            onClickHandler={() => navigate(AppRoutes.SIGNIN)}
                        >
                            Go to login
                        </AuthMethodButton>
                    </InviteCard>
                </AuthColumn>
            );
        }

        if (landing && isInviteLoginLanding(landing)) {
            return (
                <AuthColumn>
                    <FullLogo />
                    <HeaderSection>
                        <AuthTitle>{getPageTitle(landing)}</AuthTitle>
                    </HeaderSection>
                    <InviteCard>
                        <AuthSubtitle>{getWelcomeMessage(landing)}</AuthSubtitle>
                        <AuthMethodButton
                            $type="blue"
                            tooltipMessage=""
                            onClickHandler={onContinueClick}
                        >
                            Continue to workspace
                        </AuthMethodButton>
                    </InviteCard>
                </AuthColumn>
            );
        }

        return null;
    }, [loaded, error, errorLanding, landing, onContinueClick, navigate]);

    return (
        <InvitePageContainer>
            <AuthInnerContainer>
                {content}
            </AuthInnerContainer>
        </InvitePageContainer>
    );
});

const InvitePageContainer = styled(AuthPageContainer)`
    min-height: 0;
    height: 100%;
    box-sizing: border-box;
`;

const InviteCard = styled(AuthCard)`
    gap: 32px;
`;

const StyledInlineActionText = styled(ActionText)`
    display: inline-block;
`;

export default Invite;
